import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Share2, Copy, ExternalLink, EyeOff, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Manager-facing dialog to publish / unpublish a property's public
 * showcase page. Talks to the /api/properties/{id}/publish endpoints.
 */
export default function SharePropertyDialog({ property, open, onOpenChange, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    headline: '',
    amenities: '',
    show_address: false,
  });

  useEffect(() => {
    if (!property) return;
    // Reset form when a new property is selected. `setForm` is a
    // stable React setter — included in the deps to satisfy the
    // exhaustive-deps rule.
    setForm({
      headline: property.public_headline || '',
      amenities: (property.public_amenities || []).join(', '),
      show_address: !!property.public_show_address,
    });
  }, [property, open, setForm]);

  if (!property) return null;

  const publicUrl = property.public_slug
    ? `${window.location.origin}/p/${property.public_slug}`
    : null;

  const handlePublish = async () => {
    setSaving(true);
    try {
      const payload = {
        headline: form.headline.trim(),
        amenities: form.amenities
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
        show_address: !!form.show_address,
      };
      const res = await axios.post(
        `${API}/api/properties/${property.id}/publish`,
        payload,
        { withCredentials: true },
      );
      toast.success('Property published — share the link below.');
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!confirm(`Take the public page for "${property.name}" offline?`)) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/properties/${property.id}/unpublish`,
        {},
        { withCredentials: true },
      );
      toast.success('Public page taken offline.');
      onSaved?.({ is_public: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    toast.success('Link copied');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-[95vw] sm:max-w-[520px] max-h-[90dvh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="share-property-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 font-manrope">
            <Share2 className="h-5 w-5" />
            Share {property.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-5">
          {property.is_public && publicUrl && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
              data-testid="share-current-url"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                <Globe className="h-3.5 w-3.5" /> Live at
              </div>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm text-slate-900 truncate flex-1" title={publicUrl}>{publicUrl}</code>
                <Button size="sm" variant="ghost" onClick={copyUrl} data-testid="share-copy-url">
                  <Copy className="h-4 w-4" />
                </Button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                  data-testid="share-open-url"
                  aria-label="Open in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              {typeof property.public_view_count === 'number' && (
                <p className="text-xs text-emerald-700/80 mt-1">
                  {property.public_view_count} public view{property.public_view_count === 1 ? '' : 's'}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="share-headline">Headline (optional)</Label>
            <Input
              id="share-headline"
              value={form.headline}
              onChange={(e) => setForm((p) => ({ ...p, headline: e.target.value }))}
              placeholder="e.g. Beachfront villa with private pool"
              maxLength={140}
              data-testid="share-headline"
            />
            <p className="text-xs text-slate-500">
              Shown above the gallery on the public page.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-amenities">Amenities</Label>
            <Textarea
              id="share-amenities"
              rows={3}
              value={form.amenities}
              onChange={(e) => setForm((p) => ({ ...p, amenities: e.target.value }))}
              placeholder="WiFi, Pool, Hot tub, Ocean view"
              data-testid="share-amenities"
            />
            <p className="text-xs text-slate-500">
              Comma-separated. First two also appear in the sticky header.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <Label htmlFor="share-show-address" className="cursor-pointer">
                Show full address
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Off by default. When off, the public page shows city + region only.
              </p>
            </div>
            <Switch
              id="share-show-address"
              checked={form.show_address}
              onCheckedChange={(v) => setForm((p) => ({ ...p, show_address: v }))}
              data-testid="share-show-address"
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-white flex-shrink-0">
          {property.is_public && (
            <Button
              variant="outline"
              onClick={handleUnpublish}
              disabled={saving}
              className="mr-auto text-red-600 hover:text-red-700"
              data-testid="share-unpublish-btn"
            >
              <EyeOff className="h-4 w-4 mr-1" />
              Take offline
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Close
          </Button>
          <Button onClick={handlePublish} disabled={saving} data-testid="share-publish-btn">
            {saving ? 'Saving…' : property.is_public ? 'Save changes' : 'Publish now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
